import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';

type OverlayEntry = {
  id: string;
  close: () => void;
};

interface OverlayStackValue {
  push: (id: string, close: () => void) => void;
  pop: (id: string) => void;
  closeTop: () => boolean;
  hasOverlay: boolean;
}

const OverlayStackContext = createContext<OverlayStackValue>({
  push: () => undefined,
  pop: () => undefined,
  closeTop: () => false,
  hasOverlay: false,
});

export function OverlayStackProvider({ children }: { children: ReactNode }) {
  const stackRef = useRef<OverlayEntry[]>([]);
  const [hasOverlay, setHasOverlay] = useState(false);

  const sync = useCallback(() => {
    setHasOverlay(stackRef.current.length > 0);
  }, []);

  const push = useCallback(
    (id: string, close: () => void) => {
      stackRef.current = [...stackRef.current.filter((entry) => entry.id !== id), { id, close }];
      sync();
    },
    [sync]
  );

  const pop = useCallback(
    (id: string) => {
      stackRef.current = stackRef.current.filter((entry) => entry.id !== id);
      sync();
    },
    [sync]
  );

  const closeTop = useCallback(() => {
    const top = stackRef.current[stackRef.current.length - 1];
    if (!top) {
      return false;
    }
    stackRef.current = stackRef.current.slice(0, -1);
    sync();
    top.close();
    return true;
  }, [sync]);

  const value = useMemo(
    () => ({ push, pop, closeTop, hasOverlay }),
    [push, pop, closeTop, hasOverlay]
  );

  return <OverlayStackContext.Provider value={value}>{children}</OverlayStackContext.Provider>;
}

export function useOverlayStack(): OverlayStackValue {
  return useContext(OverlayStackContext);
}

export function useRegisterOverlay(isOpen: boolean, onClose: () => void): void {
  const id = useId();
  const { push, pop } = useOverlayStack();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    push(id, () => onCloseRef.current());
    return () => pop(id);
  }, [isOpen, id, push, pop]);
}
